/**
 * Shared phone search / filter helpers (E.164-style digit length, no partial phone queries).
 */

export const PHONE_SEARCH_ERROR_MESSAGE = "Enter a valid phone number";

const MIN_DIGITS = 10;
const MAX_DIGITS = 15;

/**
 * True if the string looks like a phone query (no letters), after removing common separators.
 */
export function isPhoneLikeSearchQuery(trimmed) {
  if (!trimmed) return false;
  if (/[a-zA-Z]/.test(trimmed)) return false;
  const collapsed = trimmed.replace(/[\s\-().]/g, "");
  if (!collapsed) return false;
  return /^\+?\d+$/.test(collapsed);
}

/**
 * Character-level allow-list only: keeps digits and common separators (space, hyphen,
 * parentheses, dot) plus '+'. Does NOT decide whether '+' is validly placed — that is the
 * job of resolveSearchQueryForApi / validatePhoneFilterValue, which need the original
 * position/count of '+' intact to reject things like "9876+543210".
 */
export function sanitizePhoneLikeSearchInput(raw) {
  if (raw == null) return "";
  let out = "";
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    if (c === "+" || /[\d\s\-().]/.test(c)) {
      out += c;
    }
  }
  return out;
}

/**
 * Combined "name or phone" search boxes: free text if any letter is present; otherwise phone-like
 * input (+, digits, spaces, hyphens, parens).
 */
export function normalizeCombinedSearchInput(raw) {
  if (raw == null) return "";
  const s = String(raw);
  if (/[a-zA-Z]/.test(s)) return s;
  return sanitizePhoneLikeSearchInput(s);
}

/**
 * True if '+' appears more than once, or appears anywhere other than the very first character.
 */
function hasMisplacedPlus(trimmed) {
  const plusCount = (trimmed.match(/\+/g) || []).length;
  return plusCount > 1 || (plusCount === 1 && !trimmed.startsWith("+"));
}

/**
 * Normalize a phone filter / standalone phone field to digits for the API.
 * Empty input is valid (clears filter).
 */
export function validatePhoneFilterValue(raw) {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) {
    return { valid: true, normalizedForApi: "", error: null };
  }
  if (hasMisplacedPlus(trimmed)) {
    return {
      valid: false,
      normalizedForApi: "",
      error: PHONE_SEARCH_ERROR_MESSAGE,
    };
  }
  const digits = sanitizePhoneLikeSearchInput(trimmed).replace(/\D/g, "");
  if (digits.length === 0 || digits.length > MAX_DIGITS) {
    return {
      valid: false,
      normalizedForApi: "",
      error: PHONE_SEARCH_ERROR_MESSAGE,
    };
  }
  return { valid: true, normalizedForApi: digits, error: null };
}

/**
 * Resolves a table / list `search_query` style value.
 * - Empty → search all.
 * - '+' anywhere other than the leading position → block fetch, surface {@link PHONE_SEARCH_ERROR_MESSAGE}.
 * - Contains letters → pass trimmed text (backend text search).
 * - Phone-like → send digits as typed (partial digit strings allowed, up to 15 digits);
 *   over 15 digits blocks fetch and surfaces {@link PHONE_SEARCH_ERROR_MESSAGE}.
 * - Valid phone → send digits only; backend does partial/substring matching.
 */
export function resolveSearchQueryForApi(raw) {
  const trimmed = (raw ?? "").trim();

  if (!trimmed) {
    return {
      apiSearchQuery: "",
      blockFetch: false,
      phoneSearchError: null,
    };
  }

  // '+' is only valid as the very first character (e.g. "9876+543210" is invalid).
  if (hasMisplacedPlus(trimmed)) {
    return {
      apiSearchQuery: "",
      blockFetch: true,
      phoneSearchError: PHONE_SEARCH_ERROR_MESSAGE,
    };
  }

  if (!isPhoneLikeSearchQuery(trimmed)) {
    // Free text (name/email) search
    return {
      apiSearchQuery: trimmed,
      blockFetch: false,
      phoneSearchError: null,
    };
  }

  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 0) {
    return {
      apiSearchQuery: "",
      blockFetch: false,
      phoneSearchError: null,
    };
  }
  if (digits.length > MAX_DIGITS) {
    return {
      apiSearchQuery: "",
      blockFetch: true,
      phoneSearchError: PHONE_SEARCH_ERROR_MESSAGE,
    };
  }
 // Normalize numbers with country code (e.g. +91 / 91)
  let normalizedDigits = digits;
  if (digits.length > 10) {
    normalizedDigits = digits.slice(-10);
  }

  return {
    apiSearchQuery: normalizedDigits,
    blockFetch: false,
    phoneSearchError: null,
  };
}

/**
 * Same as {@link resolveSearchQueryForApi}, but for WhatsApp/RCS log APIs that often match stored
 * E.164 values with a leading `+` (e.g. +601122223344 vs 601122223344).
 * We only add `+` when the user typed `+` or the number has 11+ digits (international); plain 10-digit
 * national input is left without `+` so US-style local searches stay unchanged.
 */
export function resolveLogPhoneSearchQueryForApi(raw) {
  const trimmed = (raw ?? "").trim();

  // Allow text search (template / campaign) for log pages
  if (!isPhoneLikeSearchQuery(trimmed)) {
    return {
      apiSearchQuery: trimmed,
      blockFetch: false,
      phoneSearchError: null,
    };
  }

  const r = resolveSearchQueryForApi(raw);
  if (r.blockFetch || !trimmed || !isPhoneLikeSearchQuery(trimmed)) {
    return r;
  }
  if (!r.apiSearchQuery) {
    return r;
  }
  const digits = r.apiSearchQuery;
  const useLeadingPlus = trimmed.startsWith("+") || digits.length >= 11;

  return {
    ...r,
    apiSearchQuery: useLeadingPlus ? `+${digits}` : digits,
  };
}

/**
 * Chat list WebSocket: same rules as {@link resolveSearchQueryForApi}; text queries stay lowercased.
 */
export function getConversationTopicsSocketPayload(raw) {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) {
    return { blockSend: false, query: "" };
  }
  const resolved = resolveSearchQueryForApi(raw);
  if (resolved.blockFetch) {
    return { blockSend: true, query: "" };
  }
  const q = resolved.apiSearchQuery;
  if (!isPhoneLikeSearchQuery(trimmed)) {
    return { blockSend: false, query: q.toLowerCase() };
  }
  return { blockSend: false, query: q };
}

/**
 * Normalize phone for URL query when the value is already a bare number (e.g. chat sender id).
 */
export function normalizePhoneDigitsForRequest(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  const digits = s.replace(/\D/g, "");
  if (digits.length >= MIN_DIGITS && digits.length <= MAX_DIGITS) return digits;
  return digits;
}

/**
 * Chat log search box: combined phone / template-name / message-text search.
 * - Phone-like input → same normalization as {@link resolveLogPhoneSearchQueryForApi}
 *   (digit-length validation, optional leading + for international numbers).
 * - Free text (letters present) → passed through as-is; backend matches it against
 *   template name and message text as well as sender/receiver.
 */
export function resolveChatLogCombinedSearchQueryForApi(raw) {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) {
    return { apiSearchQuery: "", blockFetch: false, phoneSearchError: null };
  }

  if (!isPhoneLikeSearchQuery(trimmed)) {
    return { apiSearchQuery: trimmed, blockFetch: false, phoneSearchError: null };
  }

  return resolveLogPhoneSearchQueryForApi(raw);
}